import { useMemo, type ReactNode } from "react";
import type { Course, CourseEdition } from "@/lib/api";
import {
  buildMonthOptions,
  editionOptionLabel,
  editionsForFilters,
  hasActiveListingFilters,
  uniqueCategories,
  type ListingFilterState,
} from "@/lib/listingFilters";

type Props = {
  title?: string;
  filters: ListingFilterState;
  onChange: (next: ListingFilterState) => void;
  courses?: Course[];
  editions?: CourseEdition[];
  showSearch?: boolean;
  showCourse?: boolean;
  showEdition?: boolean;
  showCategory?: boolean;
  showMonth?: boolean;
  searchPlaceholder?: string;
  resultCount?: number;
  totalCount?: number;
  hint?: string;
  extras?: ReactNode;
  categoryLabel?: string;
  categoryAllLabel?: string;
  courseLabel?: string;
  courseAllLabel?: string;
  /** Controles adicionales fuera del estado estándar (empresa, estado, etc.) */
  extraActive?: boolean;
  onClearExtras?: () => void;
};

export default function ListingFilters({
  title = "Filtros",
  filters,
  onChange,
  courses = [],
  editions = [],
  showSearch = true,
  showCourse = true,
  showEdition = true,
  showCategory = true,
  showMonth = true,
  searchPlaceholder = "Buscar...",
  resultCount,
  totalCount,
  hint,
  extras,
  categoryLabel = "Tipo de curso",
  categoryAllLabel = "Todos los tipos",
  courseLabel = "Curso",
  courseAllLabel = "Todos los cursos",
  extraActive = false,
  onClearExtras,
}: Props) {
  const months = useMemo(() => buildMonthOptions(24), []);
  const categories = useMemo(() => uniqueCategories(courses), [courses]);

  const coursesById = useMemo(() => new Map(courses.map((c) => [c.id, c])), [courses]);

  const courseOptions = useMemo(() => {
    if (!filters.category) return courses;
    return courses.filter((c) => c.category === filters.category);
  }, [courses, filters.category]);

  const editionOptions = useMemo(
    () => editionsForFilters(editions, filters, coursesById),
    [editions, filters, coursesById]
  );

  const active = hasActiveListingFilters(filters) || extraActive;

  const patch = (partial: Partial<ListingFilterState>) => {
    const next = { ...filters, ...partial };
    // Cascada: si cambia categoría/curso, limpia edición inválida
    if (partial.category !== undefined || partial.courseId !== undefined) {
      const scoped = editionsForFilters(
        editions,
        { courseId: next.courseId, category: next.category },
        coursesById
      );
      if (next.editionId && !scoped.some((e) => e.id === next.editionId)) {
        next.editionId = "";
      }
      if (partial.category !== undefined && next.courseId) {
        const course = coursesById.get(next.courseId);
        if (course && next.category && course.category !== next.category) {
          next.courseId = "";
          next.editionId = "";
        }
      }
    }
    onChange(next);
  };

  const clear = () => {
    onChange({
      search: "",
      courseId: "",
      editionId: "",
      category: "",
      month: "",
    });
    onClearExtras?.();
  };

  return (
    <div className="card space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
          <i className="ri-filter-3-line text-[var(--color-accent-oil)]"></i>
          {title}
        </h3>
        {active && (
          <button
            type="button"
            onClick={clear}
            className="text-xs text-[var(--color-accent)] hover:underline cursor-pointer"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {showSearch && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
              Buscar
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"></i>
              <input
                type="text"
                className="input-field pl-10"
                value={filters.search}
                onChange={(e) => patch({ search: e.target.value })}
                placeholder={searchPlaceholder}
                aria-label="Buscar"
              />
            </div>
          </div>
        )}

        {showCategory && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
              {categoryLabel}
            </label>
            <select
              className="input-field"
              value={filters.category}
              onChange={(e) => patch({ category: e.target.value })}
              aria-label="Filtrar por tipo de curso"
            >
              <option value="">{categoryAllLabel}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        )}

        {showCourse && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
              {courseLabel}
            </label>
            <select
              className="input-field"
              value={filters.courseId}
              onChange={(e) => patch({ courseId: e.target.value, editionId: "" })}
              aria-label="Filtrar por curso"
            >
              <option value="">{courseAllLabel}</option>
              {courseOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {showEdition && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
              Edición
            </label>
            <select
              className="input-field"
              value={filters.editionId}
              onChange={(e) => patch({ editionId: e.target.value })}
              aria-label="Filtrar por edición"
            >
              <option value="">Todas las ediciones</option>
              {editionOptions.map((ed) => (
                <option key={ed.id} value={ed.id}>
                  {editionOptionLabel(ed, !filters.courseId)}
                </option>
              ))}
            </select>
          </div>
        )}

        {showMonth && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mb-1 block">
              Mes
            </label>
            <select
              className="input-field"
              value={filters.month}
              onChange={(e) => patch({ month: e.target.value })}
              aria-label="Filtrar por mes"
            >
              <option value="">Todos los meses</option>
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {extras}
      </div>

      {(hint || (typeof resultCount === "number" && typeof totalCount === "number" && active)) && (
        <div className="flex items-center justify-between gap-3 flex-wrap">
          {hint ? (
            <p className="text-[11px] text-[var(--color-text-muted)]">{hint}</p>
          ) : (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Mostrando {resultCount} de {totalCount}
            </p>
          )}
          {hint && typeof resultCount === "number" && typeof totalCount === "number" && active && (
            <p className="text-[11px] text-[var(--color-text-muted)]">
              Mostrando {resultCount} de {totalCount}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
